import React, { useEffect, useRef } from 'react';
import { ArrowMoveDownCustomIcon } from '@assets/icon';
import './FeatureSuggestionDropdown.scss';

export type FeatureSuggestionItem = string | Record<string, unknown>;

export interface FeatureSuggestionDropdownProps {
  open: boolean;
  items?: FeatureSuggestionItem[];
  isLoading?: boolean;
  onSelect: (item: FeatureSuggestionItem) => void;
  onClose: () => void;
  /** Fallback icon when item has no API icon */
  defaultIcon?: React.ReactNode;
  ariaLabel?: string;
  /** Cap visible viewport to ~5 rows */
  fiveItems?: boolean;
  maxItems?: number;
  className?: string;
}

const getItemLabel = (item: FeatureSuggestionItem): string => {
  if (typeof item === 'string') return item.trim();
  return String(
    item.function_name || item.question || item.title || item.name || item.text || '',
  ).trim();
};

const getItemIconSrc = (item: FeatureSuggestionItem): string | undefined => {
  if (!item || typeof item === 'string') return undefined;
  const src =
    item.iconUrl ||
    item.imageUrl ||
    item.iconBackground ||
    (typeof item.icon === 'string' ? item.icon : undefined);
  return typeof src === 'string' && src.trim() ? src.trim() : undefined;
};

const FeatureSuggestionDropdown: React.FC<FeatureSuggestionDropdownProps> = ({
  open,
  items = [],
  isLoading = false,
  onSelect,
  onClose,
  defaultIcon,
  ariaLabel = 'Gợi ý',
  fiveItems = false,
  maxItems = 20,
  className = '',
}) => {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={rootRef}
      className={[
        'feature-suggestion-dropdown',
        fiveItems ? 'feature-suggestion-dropdown--five-items' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="listbox"
      aria-label={ariaLabel}
    >
      {isLoading ? (
        <div className="feature-suggestion-dropdown__skeleton">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="feature-suggestion-dropdown__skeleton-item" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="feature-suggestion-dropdown__empty">Không có dữ liệu</div>
      ) : (
        items.slice(0, maxItems).map((item, index) => {
          const label = getItemLabel(item);
          if (!label) return null;
          const iconSrc = getItemIconSrc(item);
          return (
            <button
              key={`feature-suggestion-${index}`}
              type="button"
              role="option"
              className="feature-suggestion-dropdown__item"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(item);
              }}
            >
              <span className="feature-suggestion-dropdown__item-left">
                {iconSrc ? (
                  <img
                    src={iconSrc}
                    alt=""
                    className="feature-suggestion-dropdown__icon"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  defaultIcon && (
                    <span className="feature-suggestion-dropdown__icon" aria-hidden>
                      {defaultIcon}
                    </span>
                  )
                )}
                <span className="feature-suggestion-dropdown__text">{label}</span>
              </span>
              <span className="feature-suggestion-dropdown__chevron" aria-hidden>
                <ArrowMoveDownCustomIcon />
              </span>
            </button>
          );
        })
      )}
    </div>
  );
};

export default FeatureSuggestionDropdown;
